// Pure mapping between a friendly "routing sensitivity" slider (0 = Strict,
// 1 = Lenient) and the L1.5 margin config {floor, margin} stored at
// flow_config.supervisor.routing. Shared by the RoutingExamplesPanel and the
// Builder supervisor node so both surfaces agree. No React/DOM deps.
//
// Strict (s=0) == today's behaviour exactly: floor 0.85, margin 0.0.
// Lenient (s=1): floor 0.55, margin 0.15 — route more, with a margin guarding
// precision. The runtime rule is: route iff best >= floor AND best-second >= margin.

export const DEFAULT_ROUTING = { floor: 0.85, margin: 0.0 };

const STRICT_FLOOR = 0.85;   // s=0
const LENIENT_FLOOR = 0.55;  // s=1
const MAX_MARGIN = 0.15;     // s=1
const FLOOR_SPAN = STRICT_FLOOR - LENIENT_FLOOR; // 0.30

const _clamp01 = (x) => Math.min(Math.max(x, 0), 1);
const _round2 = (x) => Math.round(x * 100) / 100;

// Sensitivity (0..1) -> {floor, margin}.
export function sensitivityToConfig(s) {
  const sv = _clamp01(Number.isFinite(s) ? s : 0);
  return {
    floor: _round2(STRICT_FLOOR - FLOOR_SPAN * sv),
    margin: _round2(MAX_MARGIN * sv),
  };
}

// {floor, margin} -> sensitivity (0..1). Derived from the floor (its primary
// axis); used to place the slider from a stored config.
export function configToSensitivity(cfg) {
  const floor = Number(cfg?.floor);
  if (!Number.isFinite(floor)) return 0;
  return _round2(_clamp01((STRICT_FLOOR - floor) / FLOOR_SPAN));
}

// Coerce a stored/edited config to valid numbers in [0,1]; missing -> default.
export function clampRoutingConfig(cfg) {
  const num = (v, d) => {
    const x = Number(v);
    return Number.isFinite(x) ? _clamp01(x) : d;
  };
  return {
    floor: _round2(num(cfg?.floor, DEFAULT_ROUTING.floor)),
    margin: _round2(num(cfg?.margin, DEFAULT_ROUTING.margin)),
  };
}

// Human label for a sensitivity value (slider caption).
export function sensitivityLabel(s) {
  const sv = _clamp01(Number.isFinite(s) ? s : 0);
  if (sv < 1 / 3) return "Strict";
  if (sv < 2 / 3) return "Balanced";
  return "Lenient";
}

// Read the routing config off an agent's flow_config, defaulting to today's.
export function routingFromAgent(agent) {
  const r = agent?.flow_config?.supervisor?.routing;
  return r ? clampRoutingConfig(r) : { ...DEFAULT_ROUTING };
}
